import { put, all, takeLatest, call } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import API, { API_URL } from "../../commonUtils/API.js";
import { deleteCookie, getCookie, getExpiryDate, setCookie } from "../../commonUtils/cookie.helper.js";
import constants from "../../commonUtils/constants.js";
import store from "../../store/index.js";
import { decrypt, encrypt } from "../../commonUtils/crypto.helper";
import { handleApiError } from "../../commonUtils/error.handler";
import { toast } from "react-toastify";

const handleLoginSuccess = async (data, rememberData, navigate) => {
  const { authMode, token, user, refreshToken } = data.data;
  const authTokenExpiration = Date.now() + constants.auth.accessTokenExpiryIntervalDuration;

  setCookie('authToken', token, constants.auth.refreshTokenDuration);
  setCookie('authTokenExpiration', authTokenExpiration, constants.auth.refreshTokenDuration);

  // console.log("authToken changed")
  // console.log("now", new Date(Date.now()))
  // console.log("authTokenExpiration", new Date(authTokenExpiration))

  if (authMode === "header" && refreshToken) {
    sessionStorage.setItem("refreshToken", refreshToken);
    const refreshTokenExpiryDate = getExpiryDate(constants.auth.refreshTokenDuration);
    const refreshTokenExpiryUtc = refreshTokenExpiryDate?.toUTCString?.() || "";
    sessionStorage.setItem("refreshTokenExpires", refreshTokenExpiryUtc);
  }

  if (user) {
    const userData = encrypt(JSON.stringify(user));
    setCookie('_u0_', userData, constants.auth.refreshTokenDuration);
  }

  if (rememberData) {
    const secureRememberData = {
      storedEmail: rememberData.storedEmail
    };
    setCookie('rememberXanadu', encrypt(JSON.stringify(secureRememberData)), constants.auth.refreshTokenDuration);
  } else {
    deleteCookie('rememberXanadu');
  }
};

const handleLogoutSuccess = (navigate, azureAccount=false, logoutUrl) => {
  store.dispatch({ type: actionTypes.CLEAR_COOKIES_AND_SESSIONS });
  if(azureAccount){
    window.location.href = logoutUrl;
  } else {
    navigate("/");
  }
};

function* handleSSOLoginSaga({ payload }) {
  // Handle redirect case (from login component)
  if (payload.type === 'redirect') {
    try {
      const cookiesOk = yield call(testCookieSupportSaga);
      const authMode = cookiesOk ? "cookie" : "header";
      window.location.href = `${API_URL}/api/v1/sso/login?authMode=${authMode}`;
    } catch (error) {
      console.error("Cookie test failed, defaulting to cookie mode:", error);
      window.location.href = `${API_URL}/api/v1/sso/login?authMode=cookie`;
    }
    return;
  }

  if (payload.data.success) {
    const { data, navigate } = payload;
    yield put({ type: actionTypes.LOGIN_SUCCESS, payload: data.data });
    yield handleLoginSuccess(data, false, navigate);
  } else {
    const { data, navigate } = payload;
    yield put({ type: actionTypes.LOGIN_FAILURE, payload: data.data || { message: "Internal server error" } });
    navigate("/");
  }
};

function* handleSSOCallbackSaga({ payload }) {
  try {
    const { token, user, authMode, refreshToken, refreshTokenExpires } = payload;
    
    // Store tokens based on authMode
    if (authMode === "header" && refreshToken) {
      sessionStorage.setItem("refreshToken", refreshToken);
      sessionStorage.setItem("refreshTokenExpires", refreshTokenExpires);
    }
    
    // Set auth token cookie (similar to regular login)
    const authTokenExpiration = Date.now() + constants.auth.accessTokenExpiryIntervalDuration;
    setCookie('authToken', token, constants.auth.refreshTokenDuration);
    setCookie('authTokenExpiration', authTokenExpiration, constants.auth.refreshTokenDuration);
    
    // Store user data
    if (user) {
      const userData = encrypt(JSON.stringify(user));
      setCookie('_u0_', userData, constants.auth.refreshTokenDuration);
    }
    
    yield put({ type: actionTypes.LOGIN_SUCCESS, payload: { token, user, authMode } });
    
  } catch (error) {
    console.error("SSO callback handling failed:", error);
    yield put({ type: actionTypes.LOGIN_FAILURE, payload: { message: "SSO login failed" } });
  }
}

function* testCookieSupportSaga() {
  try {
    // First call to set the cookie
    const { data: firstCall } = yield call(API.get, "/cookie-test");
    
    if (firstCall?.needsRetest) {
      // Wait a moment and test again to see if cookie was received
      yield new Promise(resolve => setTimeout(resolve, 100));
      const { data: secondCall } = yield call(API.get, "/cookie-test");
      return secondCall?.cookieReceived === true;
    }
    
    return firstCall?.cookieReceived === true;
  } catch (err) {
    console.error("Cookie test failed:", err);
    return false;
  }
}

function* performLoginActionSaga(action) {
  const { params } = action;
  const { navigate, rememberme, formData } = params;
 
  try {
    yield put({ type: actionTypes.SET_LOADING, payload: true });
    const cookiesOk = yield call(testCookieSupportSaga);
    const authMode = cookiesOk ? "cookie" : "header";
    const loginPayload = { ...formData, authMode };
    const { data } = yield API.post("/login", loginPayload);

    if (data.success) {
      yield put({ type: actionTypes.LOGIN_SUCCESS, payload: data.data });
      const rememberData = { 
        storedEmail: formData.email, 
        storedPassword: formData.password 
      };
      
      if(rememberme) {
        yield handleLoginSuccess(data, rememberData, navigate);
      } else {
        yield handleLoginSuccess(data, false, navigate);
      }
    } else {
      console.error("An error occurred during login:", data);
      yield put({ type: actionTypes.LOGIN_FAILURE, payload: data });
    }
 
  } catch (error) {
    const data = error?.response?.data;
    let message = "";
    if (error?.response?.status === 404) {
      message = "User not found";
    } else if (error?.response?.status === 401) {
      message = data?.message !== "Invalid credentials" ? data?.message : "";
    } else {
      message = "Internal server error";
    }
    yield put({ type: actionTypes.LOGIN_FAILURE, payload: { ...data, message: message } });
  } finally {
    yield put({ type: actionTypes.SET_LOADING, payload: false });
  }
};

function* performLogoutActionSaga(action) {
  const { payload } = action;
  const { navigate, logoutType } = payload;
  let apiroute = "/logout";
  
  if(logoutType === 'sso') {
    const userCookieData = getCookie("_u0_");
    if (userCookieData) {
      const userData = JSON.parse(decrypt(userCookieData));
      // console.log("userData", userData);
      if (userData.azure_account) apiroute = "/sso/logout";
    }
  }

  try {
    yield put({ type: actionTypes.SET_LOADING, payload: true });
    const { data } = yield API.post(apiroute);

    if (data.success) {
      yield put({
        type: actionTypes.LOGOUT_SUCCESS,
        payload: data,
      });
      handleLogoutSuccess(navigate, apiroute==="/sso/logout", data?.data?.logoutUrl || "");
    } else {
      yield put({
        type: actionTypes.LOGOUT_FAILURE,
        errorMessage: "There was an error while logging out",
      });
    }

  } catch (error) {
    console.log("Error during logout:", error);
    yield put({ type: actionTypes.SET_SHOW_LOGOUT_ALERT, payload: true });

    yield put({
      type: actionTypes.LOGOUT_FAILURE,
      errorMessage: "There was an error while logging out",
    });
  } finally {
    yield put({ type: actionTypes.SET_LOADING, payload: false });
  }
};

function* clearCookiesAndSessionsSaga() {
  localStorage.clear();
  sessionStorage.clear();
  deleteCookie('_u0_');
  deleteCookie("authToken");
  deleteCookie("authTokenExpiration");
  // deleteCookie('rememberXanadu');

  yield put({ type: actionTypes.SET_REFRESH_TOKEN_INTERVAL_ID, payload: null });
};

function* fetchMicrosoftUsersSaga() {
  try {
      const { data } = yield API.get("/fetch-microsoft-users");
      if (data.success) {
        yield put({ type: actionTypes.FETCH_MICROSOFT_USERS_SUCCESS, payload: data.data });
      } else {
        const msg = "Could not fetch Microsoft users";
        toast.error(data?.message || msg);
        yield put({ type: actionTypes.FETCH_MICROSOFT_USERS_FAILURE, message: data?.message || msg });
      }
  } catch (error) {
      const msg = "Could not fetch Microsoft users";
      handleApiError(error, msg);
      yield put({ type: actionTypes.FETCH_MICROSOFT_USERS_FAILURE, message: msg });
  }
}

function* AuthSaga() {
  yield all([
    takeLatest(actionTypes.LOGIN, performLoginActionSaga),
    takeLatest(actionTypes.LOGOUT, performLogoutActionSaga),
    takeLatest(actionTypes.HANDLE_SSO_LOGIN, handleSSOLoginSaga),
    takeLatest(actionTypes.HANDLE_SSO_CALLBACK, handleSSOCallbackSaga),
    takeLatest(actionTypes.CLEAR_COOKIES_AND_SESSIONS, clearCookiesAndSessionsSaga),
    takeLatest(actionTypes.FETCH_MICROSOFT_USERS, fetchMicrosoftUsersSaga),
  ]);
}

export default AuthSaga;